/**
 * © 2025 Indikin.online. All rights reserved.
 * Made with ❤️ by YalaCoder.
 *
 * Scroll To Top Utility
 *
 * - REWRITE SUMMARY (V2 - ROBUST PROGRESSIVE SCROLL)
 * - MISSION: To provide a bulletproof scrolling mechanism that handles lazy-loaded content,
 * layout shifts, and deep linking for ALL hash targets, not just the footer.
 *
 * - PROBLEM: When navigating to a specific section (especially from AI chat or external links),
 * lazy-loaded components (like lists or images) can load *after* the initial scroll, changing
 * the page height and leaving the user stuck halfway.
 *
 * - SOLUTION: "Progressive Scrolling"
 * Instead of scrolling once, we intentionally "chase" the target position for a few seconds.
 * 1. We identify the target (specific ID or bottom of page).
 * 2. We enter a polling loop (approx 5 seconds).
 * 3. In every iteration, we re-calculate the target's position and scroll to it.
 * 4. This ensures that even if the page stretches due to lazy loading, the viewport
 *    adjusts to keep the target in view.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const ScrollToTop = () => {
  // Extracts the pathname and hash from the current location.
  const { pathname, hash } = useLocation();

  // This effect will run every time the pathname or hash changes.
  useEffect(() => {
    // 1. Handle Hash Scroll (Deep Linking)
    if (hash) {
      const rawId = decodeURIComponent(hash.replace('#', ''));
      let userStopped = false;
      let resizeObserver = null;
      let safetyTimer = null;

      const stopScroll = () => {
        userStopped = true;
        if (resizeObserver) resizeObserver.disconnect();
        if (safetyTimer) clearTimeout(safetyTimer);
        window.removeEventListener('wheel', stopScroll);
        window.removeEventListener('touchmove', stopScroll);
        window.removeEventListener('keydown', stopScroll);
      };

      window.addEventListener('wheel', stopScroll);
      window.addEventListener('touchmove', stopScroll);
      window.addEventListener('keydown', stopScroll);

      const performScroll = () => {
        if (userStopped) return;

        // CASE A: Footer / Bottom
        if (rawId.toLowerCase() === 'footer') {
          const currentHeight = document.documentElement.scrollHeight;
          window.scrollTo({ top: currentHeight, behavior: 'smooth' });
        }
        // CASE B: Specific Element
        else {
          const element = document.getElementById(rawId);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }
      };

      // Initial Scroll
      // Small delay to ensure initial layout is done
      setTimeout(performScroll, 0);

      // Progressive Scroll using ResizeObserver (Event-driven instead of polling)
      resizeObserver = new ResizeObserver(() => {
        performScroll();
      });

      // Observe the document body for height changes (lazy loading)
      resizeObserver.observe(document.body);

      // Stop observing after 5 seconds to free resources
      safetyTimer = setTimeout(() => {
        if (resizeObserver) resizeObserver.disconnect();
        stopScroll(); // Cleanup listeners
      }, 5000);

      return () => {
        stopScroll();
      };
    }

    // 2. Handle Normal Page Navigation (Scroll to Top)
    else {
      window.scrollTo(0, 0);
    }
  }, [pathname, hash]); // Dependencies: run on URL change

  // This component does not render anything to the DOM.
  return null;
};

export default ScrollToTop;
